import { useCallback, useRef, useState } from "react";
import instance from "api/globalaxios";

/**
 * AI Proofread, as the order sheet does it (OrderDrafting.js handleProofread):
 * the document's plain text goes to /bac/arbitrator/cases/<id>/review-text/,
 * and each suggestion whose original wording is still in the HTML replaces
 * it there - the markup around the words is left alone.
 *
 * @param {object} options
 * @param {*} options.disputeId the case; without it proofreading is off
 * @param {() => string} options.getHtml the document as it stands
 * @param {(html: string) => void} options.setHtml
 * @returns {{ state: "idle"|"loading"|"done"|"error", message: string,
 *   run: () => void }}
 */
export default function useProofread({ disputeId, getHtml, setHtml }) {
  const [state, setState] = useState("idle");
  const [message, setMessage] = useState("");
  const latest = useRef({});
  latest.current = { disputeId, getHtml, setHtml };

  const run = useCallback(async () => {
    const { disputeId: id, getHtml: read } = latest.current;
    if (!id) return;
    const plain = String(read() || "")
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (plain.length < 20) {
      setState("error");
      setMessage("Editor content is too short to review (minimum 20 characters).");
      return;
    }

    setState("loading");
    setMessage("");
    try {
      const res = await instance.post(`/bac/arbitrator/cases/${id}/review-text/`, {
        text: plain,
      });
      const suggestions = res.data?.suggestions || [];
      // Applied to the document as it is now, not as it was sent: the drafter
      // may have typed meanwhile.
      let content = latest.current.getHtml() || "";
      let applied = 0;
      suggestions.forEach((s) => {
        if (s.original && s.suggestion && content.includes(s.original)) {
          content = content.replace(s.original, s.suggestion);
          applied += 1;
        }
      });
      if (applied) latest.current.setHtml(content);
      setState("done");
      setMessage(`Done — ${applied} correction${applied !== 1 ? "s" : ""} applied.`);
    } catch (err) {
      setState("error");
      setMessage(
        err?.response?.status === 403
          ? "You do not have access to this case."
          : err?.response?.data?.error || "AI service unavailable, please try again."
      );
    }
  }, []);

  return { state, message, run };
}
